import { ScoringHistoryEntry } from "../../schema/score.schema";

/**
 * One manual override as the change log shows it: the company and persona
 * rows written for the same lead at the same moment, side by side.
 */
export interface HistoryGroup {
	key: string;
	company?: ScoringHistoryEntry;
	persona?: ScoringHistoryEntry;
}

/** Pairs the company and persona entries of one override, in the order they arrived. */
export const groupHistory = (rows: ScoringHistoryEntry[]): HistoryGroup[] => {
	const groups = new Map<string, HistoryGroup>();

	for (const row of rows) {
		const entityId = row.prospectId ?? row.organizationId;
		const key = `${row.fromType}-${entityId ?? row.id}-${row.createdAt}`;
		const group = groups.get(key) ?? { key };

		if (row.type === "company") {
			group.company = row;
		} else {
			group.persona = row;
		}

		groups.set(key, group);
	}

	return Array.from(groups.values());
};

/** The entry that carries the lead's name, date and author for a group. */
export const groupLead = (group: HistoryGroup): ScoringHistoryEntry =>
	(group.company ?? group.persona) as ScoringHistoryEntry;

/** Whether the persona's classification or priority changed — each shows only when it did. */
export const personaChanges = (
	group: HistoryGroup
): { resultChanged: boolean; priorityChanged: boolean } => {
	const persona = group.persona;

	if (!persona) {
		return { resultChanged: false, priorityChanged: false };
	}

	const previous = persona.previousOverallScore;
	const next = persona.newOverallScore;

	return {
		resultChanged: persona.previousResult !== persona.newResult,
		priorityChanged: (!!previous || !!next) && previous !== next,
	};
};
